import {
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { MENU, MENU_HINTS } from '../../data/menu.data';
import { Menu } from '../../models/menu.model';
import { RUTA_ACCESO } from '../../guards/auth.guard';
import { AuthService } from '../../services/auth.service';
import { UiService } from '../../services/ui.service';
import { ThemeService } from '../../services/theme.service';

/** Seccion en la que esta parado el usuario, para el breadcrumb de la barra. */
export interface ICrumb {
  icon: string;
  title: string;
  hint: string;
}

/**
 * Barra superior de cristal: hamburguesa, breadcrumb de la seccion actual y, a
 * la derecha, el meta de encuestas, el interruptor de tema y el perfil.
 *
 * La hamburguesa hace dos cosas distintas segun el modo del panel (abre un cajon
 * en `overlay`, contrae o expande en escritorio) y su nombre accesible lo dice.
 */
@Component({
  selector: 'app-main-header',
  templateUrl: './main-header.component.html',
  styleUrl: './main-header.component.scss',
  standalone: false,
})
export class MainHeaderComponent {

  public profileOpen: boolean = false;

  public readonly authService: AuthService = inject(AuthService);
  public readonly uiService: UiService = inject(UiService);
  public readonly themeService: ThemeService = inject(ThemeService);

  /** Seccion vigente. Se recalcula en cada navegacion. */
  public readonly crumb: WritableSignal<ICrumb> = signal<ICrumb>(this.resolveCrumb('/'));

  /** El mismo boton gobierna dos cosas distintas; el nombre no puede ser el
   *  mismo en las dos, porque quien lo oye no ve el panel. */
  public readonly menuLabel: Signal<string> = computed<string>(() => {
    if (this.uiService.sidebarMode() === 'overlay') {
      return this.uiService.sidebarOpen() ? 'Cerrar el menú' : 'Abrir el menú';
    }

    return this.uiService.sidebarMode() === 'rail'
      ? 'Expandir el panel lateral'
      : 'Contraer el panel lateral';
  });

  /** En `overlay` lo desplegado es el cajon; en escritorio, el panel ancho. */
  public readonly menuExpanded: Signal<boolean> = computed<boolean>(() =>
    this.uiService.sidebarMode() === 'overlay'
      ? this.uiService.sidebarOpen()
      : !this.uiService.sidebarCollapsed());

  @ViewChild('hamburger') private hamburgerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('avatar') private avatarRef?: ElementRef<HTMLButtonElement>;

  private readonly document: Document = inject(DOCUMENT);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly router: Router = inject(Router);

  /** Valor anterior del cajon: hace falta para detectar el cierre, no el estado. */
  private drawerWasOpen: boolean = false;

  constructor() {
    this.crumb.set(this.resolveCrumb(this.router.url));

    this.router.events
      .pipe(
        filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event: NavigationEnd) => this.crumb.set(this.resolveCrumb(event.urlAfterRedirects)));

    effect(() => this.restoreFocusOnClose());
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR API PUBLICA                            */
  /* -------------------------------------------------------------------------- */

  public toggleProfile(): void {
    this.profileOpen = !this.profileOpen;
  }

  /**
   * Cierra la sesión y sale del tablero. El menú y el cajón se cierran antes de
   * navegar: el menú, para que no quede pintado sobre la pantalla de acceso
   * durante la transición; el cajón, porque `UiService` es de raíz y su estado
   * sobrevive a la navegación que destruye el panel.
   */
  public salir(): void {
    this.profileOpen = false;
    this.uiService.closeSidebar();
    this.authService.logout();
    this.router.navigate([RUTA_ACCESO]);
  }

  /**
   * `Escape` cierra el menú de perfil y devuelve el foco a su disparador.
   * Ahora pesa más que antes: es una de las dos salidas de sesión del tablero,
   * y un desplegable que anuncia `aria-expanded` y sólo se cierra con el ratón
   * deja al teclado sin camino de vuelta.
   */
  @HostListener('document:keydown', ['$event'])
  public onEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.profileOpen || event.defaultPrevented) {
      return;
    }

    event.preventDefault();
    this.profileOpen = false;
    this.avatarRef?.nativeElement.focus();
  }

  /** Si el foco sale del componente, `aria-expanded` dejaría de decir la
   *  verdad: el menú se cierra con él. */
  @HostListener('focusout', ['$event'])
  public onFocusOut(event: FocusEvent): void {
    const destino: Node | null = event.relatedTarget as Node | null;

    if (this.profileOpen && destino && !this.elementRef.nativeElement.contains(destino)) {
      this.profileOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.profileOpen = false;
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR FOCO DEL CAJON                          */
  /* -------------------------------------------------------------------------- */

  /**
   * Devuelve el foco a la hamburguesa cuando el cajon se cierra.
   *
   * El cajon lo cierran cuatro caminos (aspa, fondo, `Escape` y navegar) y en
   * todos el `inert` deja el foco huerfano en el `<body>`: sin esto el tabulador
   * volveria a empezar desde el principio de la pagina. Vive aqui y no en el
   * panel porque el disparador es de esta barra.
   */
  private restoreFocusOnClose(): void {
    const open: boolean = this.uiService.sidebarOpen();
    const closing: boolean = this.drawerWasOpen && !open;

    this.drawerWasOpen = open;

    if (!closing || this.uiService.sidebarMode() !== 'overlay') {
      return;
    }

    // Un ciclo despues: hasta que no se aplica `inert` el foco no se suelta.
    setTimeout(() => {
      const active: Element | null = this.document.activeElement;

      if (!active || active === this.document.body) {
        this.hamburgerRef?.nativeElement.focus();
      }
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR resolveCrumb                            */
  /* -------------------------------------------------------------------------- */

  /** La ruta manda: el menu ya sabe el titulo y la pregunta de cada seccion. */
  private resolveCrumb(url: string): ICrumb {
    const item: Menu = MENU.find((entry: Menu) => url.startsWith(entry.route)) ?? MENU[0];

    return {
      icon: item.icon ?? '',
      title: item.title,
      hint: MENU_HINTS[item.route] ?? '',
    };
  }
}
